import type {FluxDispatchMetrics} from './types';

/**
 * Holds all mutable state for a metrics collection batch.
 * This state is owned by a Flux instance, ensuring no module-level side effects.
 * The FluxMetricsCollector functions are pure: they operate on the context passed to them.
 */
export type FluxMetricsContext = {
  batchDispatchMetrics: FluxDispatchMetrics[];
  batchAsyncMetrics: FluxDispatchMetrics | undefined;
  currentDispatchMetrics: FluxDispatchMetrics | undefined;
  isInDispatchSyncPhase: boolean;
  hasBatchAsyncIncrements: boolean;
};

/**
 * Creates a new, empty metrics context.
 * The Flux instance should create one of these and hold it as instance state.
 */
export function createFluxMetricsContext(): FluxMetricsContext {
  return {
    batchDispatchMetrics: [],
    batchAsyncMetrics: undefined,
    currentDispatchMetrics: undefined,
    isInDispatchSyncPhase: false,
    hasBatchAsyncIncrements: false,
  };
}

/**
 * Creates an empty metrics object with default values.
 * Used to initialize metrics collection at the start of a dispatch.
 */
export function createEmptyFluxMetrics(): FluxDispatchMetrics {
  return {
    subscriberEffectCount: 0,
    changedStoresCount: 0,
    totalRegisteredStores: 0,
    storeReducersInvokedCount: 0,
    selectorsInvokedCount: 0,
    selectorsChangedCount: 0,
    enginesInvokedCount: 0,
    totalRegisteredEngines: 0,
    rootSubscriptionsInvokedCount: 0,
    batchSize: 1,
    batchIndex: 0,
    totalDispatchDuration: 0,
  };
}

/**
 * Gets the current dispatch metrics from the context, or undefined if not in a dispatch
 * or if metrics collection is not enabled.
 */
export function getCurrentFluxMetrics(
  ctx: FluxMetricsContext | undefined
): FluxDispatchMetrics | undefined {
  return ctx?.currentDispatchMetrics;
}

export function getCurrentBatchAsyncMetrics(
  ctx: FluxMetricsContext | undefined
): FluxDispatchMetrics | undefined {
  return ctx?.batchAsyncMetrics;
}

export function hasCurrentBatchAsyncIncrements(
  ctx: FluxMetricsContext | undefined
): boolean {
  return ctx?.hasBatchAsyncIncrements ?? false;
}

/**
 * Starts a new metrics collection context for a dispatch.
 * Returns the metrics object that will be populated during the dispatch.
 */
export function startFluxMetricsCollection(
  ctx: FluxMetricsContext
): FluxDispatchMetrics {
  if (!ctx.batchAsyncMetrics) {
    ctx.batchAsyncMetrics = createEmptyFluxMetrics();
  }
  ctx.currentDispatchMetrics = createEmptyFluxMetrics();
  ctx.batchDispatchMetrics.push(ctx.currentDispatchMetrics);
  ctx.isInDispatchSyncPhase = true;
  return ctx.currentDispatchMetrics;
}

/**
 * Ends the sync phase for the current dispatch, so async increments are batched.
 */
export function endFluxMetricsSyncPhase(ctx: FluxMetricsContext): void {
  ctx.currentDispatchMetrics = undefined;
  ctx.isInDispatchSyncPhase = false;
}

/**
 * Resets all metrics collection state. Called when a batch completes.
 */
export function resetFluxMetricsCollection(ctx: FluxMetricsContext): void {
  ctx.currentDispatchMetrics = undefined;
  ctx.isInDispatchSyncPhase = false;
  ctx.hasBatchAsyncIncrements = false;
  ctx.batchDispatchMetrics.length = 0;
  ctx.batchAsyncMetrics = undefined;
}

/**
 * Increments a numeric metric by 1.
 * No-op if the context is undefined (metrics collection not enabled).
 */
export function incrementFluxMetric(
  ctx: FluxMetricsContext | undefined,
  metricKey: keyof FluxDispatchMetrics
): void {
  if (!ctx) return;
  if (ctx.isInDispatchSyncPhase && ctx.currentDispatchMetrics) {
    ctx.currentDispatchMetrics[metricKey] += 1;
    return;
  }
  if (ctx.batchAsyncMetrics) {
    ctx.batchAsyncMetrics[metricKey] += 1;
    ctx.hasBatchAsyncIncrements = true;
  }
}

/**
 * Sets a numeric metric to a specific value.
 * No-op if the context is undefined (metrics collection not enabled).
 */
export function setFluxMetric(
  ctx: FluxMetricsContext | undefined,
  metricKey: keyof FluxDispatchMetrics,
  value: number
): void {
  if (!ctx) return;
  if (ctx.isInDispatchSyncPhase && ctx.currentDispatchMetrics) {
    ctx.currentDispatchMetrics[metricKey] = value;
  }
}
